import { useMemo, useRef } from 'react'
import { useFrame } from '@react-three/fiber'
import { sampleTerrainRadius, heightAt } from '@/world/terrain'
import { playerNormal } from '@/state/playerPosition'
import { useCombatStore } from '@/state/useCombatStore'
import { WEAPONS } from '@/combat/weaponTypes'
import { activeProjectiles } from '@/combat/projectileRegistry'
import { DemonEntity } from './DemonEntity'
import { DemonState } from './types'
import type { DemonData } from './types'

// ─── Seeded RNG (same pattern as mobs/spawning.ts) ───────────────────
function mulberry32(seed: number) {
  let s = seed | 0
  return () => {
    s = (s + 0x6D2B79F5) | 0
    let t = Math.imul(s ^ (s >>> 15), 1 | s)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

// ─── Tangent frame (same as simulation.ts) ────────────────────────────
function getTangentFrame(nx: number, ny: number, nz: number) {
  let ex: number, ey: number, ez: number
  if (Math.abs(ny) < 0.9) {
    ex = -nz; ey = 0; ez = nx
  } else {
    ex = 0; ey = nz; ez = -ny
  }
  const el = Math.sqrt(ex * ex + ey * ey + ez * ez)
  ex /= el; ey /= el; ez /= el
  const fx = ny * ez - nz * ey
  const fy = nz * ex - nx * ez
  const fz = nx * ey - ny * ex
  return { ex, ey, ez, fx, fy, fz }
}

// ─── Angular distance on unit sphere ──────────────────────────────────
function angularDist(
  ax: number, ay: number, az: number,
  bx: number, by: number, bz: number,
): number {
  const dot = ax * bx + ay * by + az * bz
  return Math.acos(Math.max(-1, Math.min(1, dot)))
}

// ─── Heading from one normal toward another ───────────────────────────
function headingToward(
  fromNx: number, fromNy: number, fromNz: number,
  toNx: number, toNy: number, toNz: number,
): number {
  const { ex, ey, ez, fx, fy, fz } = getTangentFrame(fromNx, fromNy, fromNz)
  const dx = toNx - fromNx
  const dy = toNy - fromNy
  const dz = toNz - fromNz
  const projE = dx * ex + dy * ey + dz * ez
  const projF = dx * fx + dy * fy + dz * fz
  return Math.atan2(projE, projF)
}

// ─── Spawn demons on wilderness terrain ───────────────────────────────
function spawnDemons(count: number): DemonData[] {
  const rng = mulberry32(66666)
  const demons: DemonData[] = []

  const PHI = (1 + Math.sqrt(5)) / 2
  const CANDIDATES = 4000

  for (let i = 0; i < CANDIDATES && demons.length < count; i++) {
    const y = 1 - (2 * i + 1) / CANDIDATES
    const rFib = Math.sqrt(1 - y * y)
    const theta = 2 * Math.PI * i / PHI
    const nx = rFib * Math.cos(theta)
    const ny = y
    const nz = rFib * Math.sin(theta)

    const h = heightAt(nx, ny, nz)
    if (h < 0.01 || h > 0.06) continue
    if (rng() > 0.15) continue

    demons.push({
      nx, ny, nz,
      radius: sampleTerrainRadius(nx, ny, nz),
      heading: rng() * Math.PI * 2,
      state: DemonState.Idle,
      stateTimer: rng() * 5,
      health: 100,
      speed: 0,
      targetNx: nx,
      targetNy: ny,
      targetNz: nz,
    })
  }

  console.log(`[Demons] Spawned ${demons.length} demons`)
  return demons
}

// ─── AI constants ─────────────────────────────────────────────────────
const AI_TICK = 0.5
const WANDER_SPEED = 1.5
const CHASE_SPEED = 4.0
const AGGRO_RANGE = 0.008    // ~80 units
const DEAGGRO_RANGE = 0.015  // ~150 units
const CONTACT_RANGE = 0.0004 // ~4 units — close enough to hit player
const CONTACT_DAMAGE = 10    // damage per hit
const CONTACT_COOLDOWN = 1.0 // seconds between hits
const MELEE_HIT_RANGE = 0.001 // ~10 units — slightly generous for melee
const PROJECTILE_HIT_RADIUS = 4 // world units

function tickDemonAI(
  demon: DemonData,
  _dt: number,
  rng: () => number,
  playerNx: number,
  playerNy: number,
  playerNz: number,
) {
  if (demon.health <= 0) return // dead demons don't act

  const distToPlayer = angularDist(
    demon.nx, demon.ny, demon.nz,
    playerNx, playerNy, playerNz,
  )

  // Aggro
  if (demon.state !== DemonState.Attack && distToPlayer < AGGRO_RANGE) {
    demon.state = DemonState.Attack
    demon.speed = CHASE_SPEED
    demon.stateTimer = 0.5
  }

  // Attack: chase player
  if (demon.state === DemonState.Attack) {
    if (distToPlayer > DEAGGRO_RANGE) {
      demon.state = DemonState.Idle
      demon.speed = 0
      demon.stateTimer = 1 + rng() * 2
      return
    }
    demon.heading = headingToward(
      demon.nx, demon.ny, demon.nz,
      playerNx, playerNy, playerNz,
    )
    demon.speed = CHASE_SPEED
    demon.stateTimer = 0.5
    return
  }

  // Normal idle/wander
  demon.stateTimer -= AI_TICK
  if (demon.stateTimer > 0) return

  switch (demon.state) {
    case DemonState.Idle: {
      demon.state = DemonState.Moving
      demon.speed = WANDER_SPEED
      demon.heading += (rng() - 0.5) * Math.PI * 0.8
      demon.stateTimer = 3 + rng() * 5
      break
    }
    case DemonState.Moving: {
      demon.state = DemonState.Idle
      demon.speed = 0
      demon.stateTimer = 2 + rng() * 4
      break
    }
    default:
      demon.stateTimer = 2
  }
}

function moveDemon(demon: DemonData, dt: number) {
  if (demon.speed < 0.01 || demon.health <= 0) return

  const { ex, ey, ez, fx, fy, fz } = getTangentFrame(demon.nx, demon.ny, demon.nz)
  const cosH = Math.cos(demon.heading)
  const sinH = Math.sin(demon.heading)
  const vx = sinH * demon.speed
  const vz = cosH * demon.speed
  const dx = (ex * vx + fx * vz) * dt
  const dy = (ey * vx + fy * vz) * dt
  const dz = (ez * vx + fz * vz) * dt

  const newX = demon.nx * demon.radius + dx
  const newY = demon.ny * demon.radius + dy
  const newZ = demon.nz * demon.radius + dz
  const len = Math.sqrt(newX * newX + newY * newY + newZ * newZ)
  demon.nx = newX / len
  demon.ny = newY / len
  demon.nz = newZ / len
}

// ─── Height sampling stagger ─────────────────────────────────────────
const HEIGHT_STAGGER = 4

interface Props {
  count?: number
}

export function DemonRenderer({ count = 100 }: Props) {
  const demons = useMemo(() => spawnDemons(count), [count])
  const frameRef = useRef(0)
  const aiTimer = useRef(0)
  const rng = useMemo(() => mulberry32(77777), [])

  // Per-demon cooldown for contact damage to player
  const contactCooldowns = useMemo(() => new Float32Array(count), [count])
  // Per-demon hit cooldown so melee doesn't hit every frame
  const hitCooldowns = useMemo(() => new Float32Array(count), [count])

  // Track if we already applied melee damage this swing
  const lastAttackId = useRef(0)

  useFrame((_state, delta) => {
    const frame = frameRef.current++
    const dt = Math.min(delta, 0.1)
    aiTimer.current += dt

    const pNx = playerNormal.x
    const pNy = playerNormal.y
    const pNz = playerNormal.z

    // AI tick
    if (aiTimer.current >= AI_TICK) {
      aiTimer.current = 0
      for (const demon of demons) {
        tickDemonAI(demon, AI_TICK, rng, pNx, pNy, pNz)
      }
    }

    // Move all demons
    for (const demon of demons) {
      moveDemon(demon, dt)
    }

    // Staggered height sampling
    for (let i = frame % HEIGHT_STAGGER; i < demons.length; i += HEIGHT_STAGGER) {
      demons[i].radius = sampleTerrainRadius(demons[i].nx, demons[i].ny, demons[i].nz)
    }

    // ── Combat: demon → player contact damage ──
    const combatState = useCombatStore.getState()
    for (let i = 0; i < demons.length; i++) {
      const demon = demons[i]
      if (demon.health <= 0) continue

      // Tick contact cooldown
      if (contactCooldowns[i] > 0) {
        contactCooldowns[i] -= dt
      }

      // Tick hit cooldown
      if (hitCooldowns[i] > 0) {
        hitCooldowns[i] -= dt
      }

      // Deal damage to player when in attack state + close
      if (demon.state === DemonState.Attack && contactCooldowns[i] <= 0) {
        const dist = angularDist(demon.nx, demon.ny, demon.nz, pNx, pNy, pNz)
        if (dist < CONTACT_RANGE) {
          useCombatStore.getState().takeDamage(CONTACT_DAMAGE)
          contactCooldowns[i] = CONTACT_COOLDOWN
        }
      }
    }

    // ── Combat: player → demon melee hit detection ──
    if (combatState.isAttacking) {
      const weapon = WEAPONS[combatState.equippedWeapon]
      if (weapon.category === 'melee') {
        // Only hit during the slash phase (0.20 - 0.55 of attack duration)
        const attackDuration = 1 / weapon.attackSpeed * 0.75
        const t = combatState.attackTimer / attackDuration
        if (t >= 0.20 && t <= 0.55) {
          // Use a swing ID to avoid multi-hitting in one swing
          const swingId = Math.floor(combatState.attackTimer * 1000)
          if (swingId !== lastAttackId.current) {
            lastAttackId.current = swingId

            let dmg = weapon.damage
            if (combatState.isPowerStriking) dmg *= 3

            for (let i = 0; i < demons.length; i++) {
              const demon = demons[i]
              if (demon.health <= 0 || hitCooldowns[i] > 0) continue

              const dist = angularDist(demon.nx, demon.ny, demon.nz, pNx, pNy, pNz)
              if (dist < MELEE_HIT_RANGE) {
                demon.health -= dmg
                hitCooldowns[i] = 0.3 // prevent multi-hit in one swing
                if (demon.health <= 0) {
                  demon.health = 0
                  demon.state = DemonState.Death
                  demon.speed = 0
                }
              }
            }
          }
        }
      }
    } else {
      // Reset swing tracker when not attacking
      lastAttackId.current = 0
    }

    // ── Combat: projectile → demon hit detection ──
    for (let pi = activeProjectiles.length - 1; pi >= 0; pi--) {
      const proj = activeProjectiles[pi]
      for (let i = 0; i < demons.length; i++) {
        const demon = demons[i]
        if (demon.health <= 0) continue

        // World position of demon
        const dwx = demon.nx * demon.radius
        const dwy = demon.ny * demon.radius
        const dwz = demon.nz * demon.radius

        const dx = proj.position.x - dwx
        const dy = proj.position.y - dwy
        const dz = proj.position.z - dwz
        const distSq = dx * dx + dy * dy + dz * dz

        if (distSq < PROJECTILE_HIT_RADIUS * PROJECTILE_HIT_RADIUS) {
          demon.health -= proj.damage
          if (demon.health <= 0) {
            demon.health = 0
            demon.state = DemonState.Death
            demon.speed = 0
          }
          // Remove projectile on hit
          activeProjectiles.splice(pi, 1)
          break
        }
      }
    }
  })

  return (
    <>
      {demons.map((demon, i) => (
        <DemonEntity key={i} data={demon} />
      ))}
    </>
  )
}
